import React, { useCallback, useMemo, useState } from 'react'
import { apiGet } from 'tennders-stracture/libs/api'
import SalesInvoiceModal from 'tennders-finance/sales-invoice/modals/SalesInvoiceModal'
import useSession from 'tennders-auth/hooks/useSession'
import { TransactionType } from 'tennders-transactions/types/TransactionType'

type FetchSalesInvociesWithTransaction = (transaction: Record<string, any>) => void

type useSalesInvoiceModalProps = {
  onClose ?: (fetch ?: FetchSalesInvociesWithTransaction) => void
  transaction ?: TransactionType
}

const useSalesInvoiceModal = ({ onClose, transaction } : useSalesInvoiceModalProps = {}) => {
  const { isBroker } = useSession()
  const [salesInvoiceData, setSalesInvoiceData] = useState(null)
  const [loading, setLoading] = useState(false)

  const fetchSalesInvoicesWithTransaction = useCallback(async (transaction) => {
    try {
      if (transaction && isBroker() && transaction.id) {
        return await apiGet(`/salesInvoice/byTransaction/${transaction.id}`)
      }
    } catch (e) {
      // TO DO Handle Catch
      console.info('e', e)
    }
    return []
  }, [])

  const showSalesInvoice = useCallback((id : number|string, force: null|boolean) => {
    setLoading(true)
    apiGet(`/salesInvoice/${id}`).then((data) => {
      setLoading(false)
      if (data) {
        if (!force && (data.generatedInvoiceNo && data.documentUrl)) {
          window.open(data.documentUrl, '_blank')
        } else {
          setSalesInvoiceData(data)
        }
      }
    }).catch(() => {
      setLoading(false)
    })
  }, [])

  const initializeSalesInvoice = useCallback(async (transaction) => {
    // if no previous sales invoice exists and we have a valid id
    // call salesInvoices/initializeWithTransaction/{transaction.id}
    // this endpoint get the loadingDate, unloadingDate, route no, price information and set them
    if (transaction && isBroker() && transaction.id) {
      setLoading(true)
      const data = await apiGet(`salesInvoice/initializeWithTransaction/${transaction.id}`)
      if (data) {
        const _data = data
        if (!_data.id) {
          _data.discount.vatPercentage = 0
        }
        setSalesInvoiceData({
          ..._data,
          transaction: transaction.id
        })
      } else {
        setSalesInvoiceData(undefined)
      }
      setLoading(false)
    } else {
      setSalesInvoiceData({ isOpen: true })
    }
  }, [])

  const initializeCreditNote = useCallback(async (transaction) => {
    // if no previous sales invoice exists and we have a valid id
    // call salesInvoices/initializeWithTransaction/{transaction.id}
    // this endpoint get the loadingDate, unloadingDate, route no, price information and set them
    if (transaction && isBroker() && transaction.id) {
      setLoading(true)
      const data = await apiGet(`salesInvoice/initializeWithTransaction/${transaction.id}`)
      if (data) {
        const _data = data
        if (!_data.id) {
          _data.discount.vatPercentage = 0
        }
        setSalesInvoiceData({
          ..._data,
          objectType: 'creditNote',
          transaction: transaction.id
        })
      } else {
        setSalesInvoiceData(undefined)
      }
      setLoading(false)
    }
  }, [])

  const showCreditNote = useCallback((id : number|string, force: null|boolean) => {
    setLoading(true)
    if (!id) {
      return initializeCreditNote(transaction)
    }
    if (typeof id === 'string') {
      id = parseInt(id, 10)
    }
    apiGet(`/creditNotes/${id}`).then((data) => {
      setLoading(false)
      if (data) {
        if (!force && (data.generatedInvoiceNo && data.documentUrl)) {
          window.open(data.documentUrl, '_blank')
        } else {
          setSalesInvoiceData({
            ...data,
            objectType: 'creditNote'

          })
        }
      }
    }).catch(() => {
      setLoading(false)
    })
  }, [])

  const onCloseCallback = useCallback(() => {
    setSalesInvoiceData(null)
    if (onClose) {
      onClose(fetchSalesInvoicesWithTransaction)
    }
  }, [onClose])

  const salesInvoiceModal = useMemo(() => {
    if (salesInvoiceData) {
      return (
        <SalesInvoiceModal
          onClose={onCloseCallback}
          isOpen={salesInvoiceData}
          initialForm={salesInvoiceData}
          status={salesInvoiceData?.status}
          selectedTransaction={transaction}
        />
      )
    }
    return null
  }, [salesInvoiceData, transaction])

  return {
    initializeSalesInvoice,
    showSalesInvoice,
    salesInvoiceData,
    salesInvoiceModal,
    showCreditNote,
    initializeCreditNote,
    fetchSalesInvociesWithTransaction: fetchSalesInvoicesWithTransaction,
    loading
  }
}

export default useSalesInvoiceModal
